import {
	ExamAttemptSummaryAction,
	GenerationStatus,
} from "@brookbush/database";

import Link from "next/link";
import posthog from "posthog-js";
import { type ReactNode, useEffect, useState } from "react";

import ExamReviewBuilding from "./ExamReviewBuilding";
import SimplePieChart from "./SimplePieChart";
import Button from "~/components/Button";
import ContentCard from "~/components/ContentCard";
import Dialog from "~/components/Dialog";
import {
	useFinalExamAttempts,
	useNextRecommended,
	useSelectedCertification,
} from "~/hooks";
import useLatestExamAttemptSummary from "~/hooks/useLatestExamAttemptSummary";

const LOADING_TIPS = [
	"Reviewing Your Exam Attempt",
	"Identifying Patterns Across Recent Attempts",
	"Generating Personalized Study Recommendations",
	"Pinpointing the Best Sections to Review",
	"Finalizing Your Coaching Plan",
];

interface ExamReviewModalProps {
	courseSlug: string;
	trigger: (openModal: () => void) => ReactNode;
}

const ExamReviewModal = ({ courseSlug, trigger }: ExamReviewModalProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [tipIndex, setTipIndex] = useState(0);

	const { selectedCertification } = useSelectedCertification();
	const { course: recommendedCourse } = useNextRecommended(
		selectedCertification?.slug,
	);
	const { data: attempts } = useFinalExamAttempts();

	const { data: summary } = useLatestExamAttemptSummary(
		isOpen ? courseSlug : undefined,
	);

	const isLoading =
		summary === undefined ||
		(summary !== null &&
			summary.status !== GenerationStatus.completed &&
			summary.status !== GenerationStatus.failed);

	useEffect(() => {
		if (!isLoading || !isOpen) return;
		const interval = setInterval(() => {
			setTipIndex((prev) => Math.min(prev + 1, LOADING_TIPS.length - 1));
		}, 5000);
		return () => clearInterval(interval);
	}, [isLoading, isOpen]);

	return (
		<>
			{trigger(() => {
				setIsOpen(true);
				setTipIndex(0);
			})}

			<Dialog
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				title="Exam Review"
				className="max-w-2xl"
			>
				{isLoading ? (
					<ExamReviewBuilding
						currentTip={LOADING_TIPS[tipIndex]}
						progress={((tipIndex + 1) / LOADING_TIPS.length) * 100}
					/>
				) : summary ? (
					<div className="flex min-h-50 animate-fade-in flex-col gap-8">
						{(summary.difficultyLevel || summary.cognitiveLevel) && (
							<div className="flex flex-col gap-4">
								<div className="flex flex-col gap-8 sm:flex-row sm:gap-4">
									{summary.difficultyLevel &&
										Object.keys(summary.difficultyLevel).length > 0 && (
											<div className="flex flex-1 flex-col items-center sm:items-start">
												<h4 className="mb-4 font-medium text-lg">
													Difficulty Level
												</h4>
												<SimplePieChart data={summary.difficultyLevel} />
												<div className="mt-2 flex w-full flex-col gap-3">
													{Object.entries(summary.difficultyLevel).map(
														([level, count]) => (
															<div
																key={level}
																className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800/50"
															>
																<span className="font-medium text-slate-700 capitalize dark:text-slate-300">
																	{level}
																</span>
																<div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
																	<span className="font-semibold text-slate-900 dark:text-slate-100">
																		{count}
																	</span>
																	<span>incorrect</span>
																</div>
															</div>
														),
													)}
												</div>
											</div>
										)}

									{summary.cognitiveLevel &&
										Object.keys(summary.cognitiveLevel).length > 0 && (
											<div className="flex flex-1 flex-col items-center sm:items-start">
												<h4 className="mb-4 font-medium text-lg">
													Question Type
												</h4>
												<SimplePieChart data={summary.cognitiveLevel} />
												<div className="mt-2 flex w-full flex-col gap-3">
													{Object.entries(summary.cognitiveLevel).map(
														([level, count]) => (
															<div
																key={level}
																className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800/50"
															>
																<span className="font-medium text-slate-700 capitalize dark:text-slate-300">
																	{level}
																</span>
																<div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
																	<span className="font-semibold text-slate-900 dark:text-slate-100">
																		{count}
																	</span>
																	<span>incorrect</span>
																</div>
															</div>
														),
													)}
												</div>
											</div>
										)}
								</div>
							</div>
						)}

						<div className="flex flex-col gap-4">
							<h4 className="font-medium text-lg">Study Recommendations</h4>
							<div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-slate-700 dark:bg-slate-800">
								{summary?.attemptAnalysis && summary.studyRecommendation ? (
									<div className="prose prose-sm dark:prose-invert max-w-none">
										<p>{summary.attemptAnalysis}</p>
										<p>{summary.studyRecommendation}</p>
									</div>
								) : (
									<p className="text-slate-500">Failed to generate summary</p>
								)}
							</div>

							<div className="flex flex-row items-center justify-center">
								{summary.actionIntent === ExamAttemptSummaryAction.STUDY_MORE ||
								summary.actionIntent ===
									ExamAttemptSummaryAction.WAIT_AND_STUDY ? (
									<Link href={`/courses/${courseSlug}`}>
										<Button size="lg" variant="outlined" className="mt-2">
											Return to Course
										</Button>
									</Link>
								) : summary.actionIntent ===
									ExamAttemptSummaryAction.TAKE_FINAL ? (
									attempts?.remainingAttempts &&
									attempts?.remainingAttempts > 0 ? (
										<Link href={`/courses/${courseSlug}/exams/final`}>
											<Button size="lg" variant="outlined" className="mt-2">
												Take Final Exam
											</Button>
										</Link>
									) : (
										<Link href={`/courses/${courseSlug}`}>
											<Button size="lg" variant="outlined" className="mt-2">
												Return to Course
											</Button>
										</Link>
									)
								) : recommendedCourse?.slug ? (
									<div className="flex w-full flex-col gap-3">
										<h4 className="font-medium text-lg">
											Next Recommended Course:
										</h4>
										<div className="flex flex-col">
											{recommendedCourse && (
												<ContentCard
													isRecommended
													{...recommendedCourse}
													_type="COURSE"
												/>
											)}
										</div>
									</div>
								) : (
									<Link href="/courses/">
										<Button size="lg" variant="outlined" className="mt-2">
											Return to Courses
										</Button>
									</Link>
								)}
							</div>
						</div>

						{summary.focusSections && summary.focusSections.length > 0 && (
							<div className="flex flex-col gap-4">
								<h4 className="font-medium text-lg">
									Based on your exam results, we recommend reviewing the
									following sections:
								</h4>
								<div className="flex flex-col gap-3">
									{[...summary.focusSections]
										.sort((a, b) => a.priority - b.priority)
										.map((section) => (
											<Link
												key={section.subSectionKey ?? section.sectionKey}
												href={`/courses/${courseSlug}#${
													section.subSectionKey ?? section.sectionKey
												}`}
												onClick={() => {
													posthog.capture("exam_coach:section_link_clicked");
												}}
												target={"_blank"}
												className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
											>
												<div>
													<div className="font-medium">
														{section.subSectionTitle ?? section.sectionTitle}
													</div>

													{section.subSectionKey && (
														<div className="text-slate-500 text-sm dark:text-slate-400">
															{section.sectionTitle}
														</div>
													)}
												</div>

												<div className="prose prose-sm dark:prose-invert max-w-none">
													<p>{section.rationale}</p>
												</div>
											</Link>
										))}
								</div>
							</div>
						)}
					</div>
				) : (
					<div className="flex min-h-50 items-center justify-center p-8 text-slate-500">
						No review data available.
					</div>
				)}
			</Dialog>
		</>
	);
};

export default ExamReviewModal;
